/**
 * Where a fitted page lands when the drawing area changes size (Issue #2920).
 *
 * While a drawer animates or the window is resized, re-rendering the page on every frame is far too
 * slow, so the painted canvas is scaled and moved with a CSS transform to where the fitted page will
 * be, and the sharp re-render follows once the size settles. These functions compute that place one
 * axis at a time, mirroring the Preview layout: the canvas has a fixed margin and is centred in a
 * wrapper that is at least as large as the drawing area, so on an axis where the page fits it is
 * centred, and on an axis where it overflows its position comes from the scroll offset.
 */

/** One axis of the canvas's untransformed layout, measured from the drawing area's visible edge. */
export interface PreviewAxis {
  /** Visible length of the drawing area on this axis. */
  viewport: number;
  /** Position of the canvas's leading edge, before any transform. */
  start: number;
  /** Displayed length of the canvas now. */
  size: number;
  /** Displayed length of the canvas once the fit is rendered. */
  targetSize: number;
  /** Margin the layout keeps around the canvas. */
  margin: number;
}

/**
 * The canvas point that should stay put on this axis: the one under the middle of the view.
 *
 * Measured in the canvas's current displayed pixels, which is what PdfRenderService.setZoom takes as
 * its anchor, so the final render keeps the same point in the middle as the preview did.
 */
export function viewCentreOnCanvas(axis: Pick<PreviewAxis, 'viewport' | 'start'>): number {
  return axis.viewport / 2 - axis.start;
}

/** How much larger the fitted page is than the painted one, measured on one axis. */
function sizeRatio(axis: PreviewAxis): number {
  return axis.size > 0 ? axis.targetSize / axis.size : 1;
}

/**
 * Where the canvas's leading edge goes once the fit is applied.
 *
 * If the fitted page and its margins fit in the view, the layout centres it. Otherwise the point
 * under the middle of the view stays there, limited so the page cannot be pulled further in than its
 * margin, which is as far as scrolling can take it.
 */
export function fittedStart(axis: PreviewAxis, ratio = sizeRatio(axis)): number {
  if (axis.targetSize + 2 * axis.margin <= axis.viewport) return (axis.viewport - axis.targetSize) / 2;
  const middle = axis.viewport / 2;
  const anchored = middle - viewCentreOnCanvas(axis) * ratio;
  return Math.min(axis.margin, Math.max(axis.viewport - axis.targetSize - axis.margin, anchored));
}

/**
 * CSS transform, applied with a `0 0` origin, that draws the canvas where the fitted page will be.
 * Both axes use the one ratio of the scale, because the page keeps its proportions; taking each
 * axis's own ratio would let rounding in the two sizes place one axis slightly off the scaled page.
 */
export function resizePreviewTransform(x: PreviewAxis, y: PreviewAxis): string {
  const ratio = sizeRatio(x);
  const dx = fittedStart(x, ratio) - x.start;
  const dy = fittedStart(y, ratio) - y.start;
  return `translate3d(${dx}px, ${dy}px, 0) scale3d(${ratio}, ${ratio}, 1)`;
}
